import {
  AccountSelector,
  Alert,
  AnimatedContentLoader,
  BetaPill,
  Button,
  ModalV2,
  TextTooltip
} from '@components'
import { useBusinessContext } from '@context'
import { SettingsAccountField } from '@pages/SettingsPage/components/SettingsAccountField'
import { SettingsBooleanValue } from '@pages/SettingsPage/components/SettingsBooleanValue'
import { SettingsDataCard } from '@pages/SettingsPage/components/SettingsDataCard'
import { SettingsReadOnlyField } from '@pages/SettingsPage/components/SettingsReadOnlyField'
import { SettingsSubPageLayout } from '@pages/SettingsPage/components/SettingsSubPageLayout'
import { SettingsSwitchField } from '@pages/SettingsPage/components/SettingsSwitchField'
import {
  fetchSalaxyAccountMappings,
  fetchSalaxySettings,
  ISalaxyAccountMapping,
  ISalaxySettings,
  replaceSalaxyAccountMappings,
  updateSalaxySettings
} from '@query/salaxy'
import { applyHttpFormErrors } from '@utils'
import { Controller, useForm } from 'react-hook-form'
import React, { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import {
  FaArrowRight,
  FaBookOpen,
  FaCalculator,
  FaPen,
  FaQuestionCircle,
  FaSave
} from 'react-icons/fa'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import styled from 'styled-components'

type MappingDraft = {
  salaxy_account_number: string
  salaxy_account_name: string
  account_id: number | null
  resolved_account_id: number | null
  dirty: boolean
}

const BetaSettingLabel: React.FC<{ text: string }> = ({ text }) => {
  const [t] = useTranslation()
  return (
    <BetaLabelRow>
      <span>{text}</span>
      <BetaPill label={t('general.beta.label')} tooltip={t('general.beta.tooltip')} />
    </BetaLabelRow>
  )
}

const mappingValue = (mapping: ISalaxyAccountMapping): number | undefined =>
  mapping.account_id ?? mapping.resolved_account_id ?? undefined

const mappingRowLabel = (row: {
  salaxy_account_number: string
  salaxy_account_name: string
}): string => {
  const name = row.salaxy_account_name.trim()
  if (!name) {
    return row.salaxy_account_number
  }
  return `${row.salaxy_account_number} ${name}`
}

const bookingConfirmDescription = (
  t: (key: string) => string,
  pending: ISalaxySettings | null,
  settings: ISalaxySettings | undefined
): string => {
  if (pending == null) {
    return t('settings.salaxy.bookingConfirmBody')
  }
  const enablingAccrual =
    Boolean(pending.has_accrual_based_salaries) && !settings?.has_accrual_based_salaries
  const enablingHoliday =
    Boolean(pending.has_salaxy_holiday_accruals) && !settings?.has_salaxy_holiday_accruals
  if (enablingHoliday && !enablingAccrual) {
    return t('settings.salaxy.bookingConfirmBodyHoliday')
  }
  if (enablingAccrual && !enablingHoliday) {
    return t('settings.salaxy.bookingConfirmBodyAccrual')
  }
  return t('settings.salaxy.bookingConfirmBody')
}

export const SalaxySettings: React.FC = () => {
  const [t] = useTranslation()
  const queryClient = useQueryClient()
  const { slug, data: business } = useBusinessContext()
  const [isBookingModalVisible, setIsBookingModalVisible] = useState(false)
  const [isMappingModalVisible, setIsMappingModalVisible] = useState(false)
  const [pendingBookingValues, setPendingBookingValues] = useState<ISalaxySettings | null>(null)
  const [mappingDraft, setMappingDraft] = useState<MappingDraft[]>([])
  const salaxyEnabled = Boolean(business?.salaxy_enabled)

  const { data: settings, isLoading: isSettingsLoading } = useQuery({
    queryKey: [slug, 'salaxy', 'settings'],
    queryFn: () => fetchSalaxySettings({ slug }),
    enabled: salaxyEnabled
  })

  const { data: mappings, isLoading: isMappingsLoading } = useQuery({
    queryKey: [slug, 'salaxy', 'account-mappings'],
    queryFn: () => fetchSalaxyAccountMappings({ slug }),
    enabled: salaxyEnabled
  })

  const { control, handleSubmit, reset, setError } = useForm<ISalaxySettings>({
    defaultValues: {
      has_accrual_based_salaries: false,
      has_salaxy_holiday_accruals: false
    }
  })

  useEffect(() => {
    if (settings) {
      reset(settings)
    }
  }, [reset, settings])

  const resetMappingDraft = () => {
    setMappingDraft(
      (mappings ?? []).map(mapping => ({
        salaxy_account_number: mapping.salaxy_account_number,
        salaxy_account_name: mapping.salaxy_account_name,
        account_id: mapping.account_id,
        resolved_account_id: mapping.resolved_account_id,
        dirty: false
      }))
    )
  }

  const updateSettingsMutation = useMutation({
    mutationFn: (values: ISalaxySettings) => updateSalaxySettings({ slug, ...values }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [slug] })
      toast.success(t('general.changesSaved'))
      setIsBookingModalVisible(false)
      setPendingBookingValues(null)
    },
    onError: error => {
      applyHttpFormErrors(error, setError)
      toast.error(t('error.oho'))
    }
  })

  const saveBookingValues = (values: ISalaxySettings) => {
    const enablingAccrual =
      Boolean(values.has_accrual_based_salaries) && !settings?.has_accrual_based_salaries
    const enablingHoliday =
      Boolean(values.has_salaxy_holiday_accruals) && !settings?.has_salaxy_holiday_accruals
    if (enablingAccrual || enablingHoliday) {
      setPendingBookingValues(values)
      return
    }
    updateSettingsMutation.mutate(values)
  }

  const saveMappingsMutation = useMutation({
    mutationFn: () =>
      replaceSalaxyAccountMappings({
        slug,
        mappings: mappingDraft.filter(row => {
          if (row.account_id == null) {
            return false
          }
          const original = (mappings ?? []).find(
            mapping => mapping.salaxy_account_number === row.salaxy_account_number
          )
          if (original?.is_explicit) {
            return true
          }
          return row.dirty
        })
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [slug, 'salaxy', 'account-mappings'] })
      toast.success(t('general.changesSaved'))
      setIsMappingModalVisible(false)
    },
    onError: () => toast.error(t('error.oho'))
  })

  const pageHeader = {
    title: t('settings.sections.palkkaus'),
    subTitle: t('settings.salaxy.pageSubtitle')
  }

  const mappingRows = mappings ?? []
  const isLoading = isSettingsLoading || isMappingsLoading

  if (!salaxyEnabled) {
    return (
      <SettingsSubPageLayout {...pageHeader}>
        <CardsWrapper>
          <SettingsDataCard
            icon={<FaCalculator />}
            title={t('settings.salaxy.enableTitle')}
            banner={<Alert description={t('settings.salaxy.enableDescription')} marginBottom={0} />}
            body={
              <Link to={`/${slug}/salaries`}>
                <Button intent="primary" size="sm" icon={<FaArrowRight />}>
                  {t('settings.salaxy.enableAction')}
                </Button>
              </Link>
            }
          />
        </CardsWrapper>
      </SettingsSubPageLayout>
    )
  }

  return (
    <SettingsSubPageLayout {...pageHeader}>
      <AnimatedContentLoader isLoading={isLoading}>
        <CardsWrapper>
          <SettingsDataCard
            icon={<FaBookOpen />}
            title={t('settings.salaxy.bookingTitle')}
            action={
              <Button
                isSecondary
                size="sm"
                icon={<FaPen />}
                onClick={() => {
                  reset(
                    settings ?? {
                      has_accrual_based_salaries: false,
                      has_salaxy_holiday_accruals: false
                    }
                  )
                  setIsBookingModalVisible(true)
                }}
              >
                {t('general.edit')}
              </Button>
            }
          >
            <SettingsReadOnlyField
              layout="row"
              label={<BetaSettingLabel text={t('settings.salaxy.accrualToggle')} />}
              description={t('settings.salaxy.accrualHint')}
              value={<SettingsBooleanValue value={settings?.has_accrual_based_salaries} />}
            />
            <SettingsReadOnlyField
              layout="row"
              label={<BetaSettingLabel text={t('settings.salaxy.holidayToggle')} />}
              description={t('settings.salaxy.holidayHint')}
              value={<SettingsBooleanValue value={settings?.has_salaxy_holiday_accruals} />}
            />
          </SettingsDataCard>

          <SettingsDataCard
            icon={<FaCalculator />}
            title={
              <CardTitleWithHint>
                <span>{t('settings.salaxy.mappingTitle')}</span>
                <TextTooltip tooltip={t('settings.salaxy.mappingInfo')}>
                  <HintIcon>
                    <FaQuestionCircle />
                  </HintIcon>
                </TextTooltip>
              </CardTitleWithHint>
            }
            action={
              <Button
                isSecondary
                size="sm"
                icon={<FaPen />}
                onClick={() => {
                  resetMappingDraft()
                  setIsMappingModalVisible(true)
                }}
              >
                {t('general.edit')}
              </Button>
            }
            body={
              mappingRows.length === 0 ? (
                <EmptyHint>{t('settings.salaxy.mappingEmpty')}</EmptyHint>
              ) : undefined
            }
          >
            {mappingRows.map(mapping => (
              <SettingsAccountField
                key={mapping.salaxy_account_number}
                label={mappingRowLabel(mapping)}
                badge={mapping.is_explicit ? undefined : t('settings.salaxy.mappingDefault')}
                accountId={mappingValue(mapping)}
              />
            ))}
          </SettingsDataCard>
        </CardsWrapper>
      </AnimatedContentLoader>

      <ModalV2
        header={t('settings.salaxy.bookingTitle')}
        isVisible={isBookingModalVisible}
        handleOnClose={() => setIsBookingModalVisible(false)}
        width={500}
        footer={
          <ModalFooter>
            <Button type="button" onClick={() => setIsBookingModalVisible(false)}>
              {t('general.cancel')}
            </Button>
            <Button
              type="submit"
              form="salaxy-settings-form"
              intent="success"
              icon={<FaSave />}
              disabled={updateSettingsMutation.isPending}
              showSpinner={updateSettingsMutation.isPending}
            >
              {t('general.save')}
            </Button>
          </ModalFooter>
        }
      >
        <form id="salaxy-settings-form" onSubmit={handleSubmit(saveBookingValues)}>
          <Controller
            control={control}
            name="has_accrual_based_salaries"
            render={({ field: { onChange, value } }) => (
              <SettingsSwitchField
                id="has_accrual_based_salaries"
                label={t('settings.salaxy.accrualToggle')}
                aside={
                  <BetaPill label={t('general.beta.label')} tooltip={t('general.beta.tooltip')} />
                }
                hint={t('settings.salaxy.accrualHint')}
                checked={Boolean(value)}
                onChange={onChange}
              />
            )}
          />
          <Controller
            control={control}
            name="has_salaxy_holiday_accruals"
            render={({ field: { onChange, value } }) => (
              <SettingsSwitchField
                id="has_salaxy_holiday_accruals"
                label={t('settings.salaxy.holidayToggle')}
                aside={
                  <BetaPill label={t('general.beta.label')} tooltip={t('general.beta.tooltip')} />
                }
                hint={t('settings.salaxy.holidayHint')}
                checked={Boolean(value)}
                onChange={onChange}
              />
            )}
          />
        </form>
      </ModalV2>

      <ModalV2
        header={t('settings.salaxy.mappingTitle')}
        isVisible={isMappingModalVisible}
        handleOnClose={() => setIsMappingModalVisible(false)}
        width={560}
        footer={
          <ModalFooter>
            <Button type="button" onClick={() => setIsMappingModalVisible(false)}>
              {t('general.cancel')}
            </Button>
            <Button
              intent="success"
              icon={<FaSave />}
              disabled={saveMappingsMutation.isPending}
              showSpinner={saveMappingsMutation.isPending}
              onClick={() => saveMappingsMutation.mutate()}
            >
              {t('general.save')}
            </Button>
          </ModalFooter>
        }
      >
        <Alert type="info" description={t('settings.salaxy.mappingInfo')} marginBottom={0} />
        {mappingDraft.length === 0 ? (
          <EmptyHint>{t('settings.salaxy.mappingEmpty')}</EmptyHint>
        ) : (
          <MappingList>
            {mappingDraft.map((row, index) => (
              <AccountSelector
                key={row.salaxy_account_number}
                label={mappingRowLabel(row)}
                value={row.account_id ?? row.resolved_account_id ?? undefined}
                onChange={accountId => {
                  setMappingDraft(current =>
                    current.map((item, itemIndex) =>
                      itemIndex === index ? { ...item, account_id: accountId, dirty: true } : item
                    )
                  )
                }}
              />
            ))}
          </MappingList>
        )}
      </ModalV2>

      <ModalV2
        header={t('settings.salaxy.bookingConfirmTitle')}
        isVisible={pendingBookingValues != null}
        handleOnClose={() => setPendingBookingValues(null)}
        width={480}
        footer={
          <ModalFooter>
            <Button type="button" onClick={() => setPendingBookingValues(null)}>
              {t('general.cancel')}
            </Button>
            <Button
              intent="success"
              icon={<FaSave />}
              disabled={updateSettingsMutation.isPending}
              showSpinner={updateSettingsMutation.isPending}
              onClick={() => {
                if (pendingBookingValues) {
                  updateSettingsMutation.mutate(pendingBookingValues)
                }
              }}
            >
              {t('general.save')}
            </Button>
          </ModalFooter>
        }
      >
        <Alert
          type="warning"
          description={bookingConfirmDescription(t, pendingBookingValues, settings)}
          marginBottom={0}
        />
      </ModalV2>
    </SettingsSubPageLayout>
  )
}

const BetaLabelRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  min-width: 0;
  flex-wrap: wrap;
`

const CardsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md}rem;
`

const CardTitleWithHint = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  min-width: 0;
`

const HintIcon = styled.span`
  display: inline-flex;
  color: ${({ theme }) => theme.colors.metalGray};
  cursor: help;

  svg {
    width: ${({ theme }) => theme.fontSize.sm}rem;
    height: ${({ theme }) => theme.fontSize.sm}rem;
  }
`

const MappingList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
  margin-top: ${({ theme }) => theme.spacing.md}rem;
`

const EmptyHint = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.metalGray};
`

const ModalFooter = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`
